// client/src/api/dashboardAPI.js
//
// M06 Fase 4 - Cliente API del dashboard admin.
//
// Refactorizado a clase para alinear con el patron de M05 (AdminUsersV2).
// Coexiste con la export `dashboardApi` (objeto plano) por compatibilidad
// con los 4 consumidores existentes; estos se migran progresivamente en
// las Fases 5-7 a usar la instancia exportada `dashboardApi`.

import { ENV } from "../Utils";

const DASH = ENV?.API_ROUTES?.DASHBOARD || {};
const ROUTES = {
  OVERVIEW: DASH.OVERVIEW || "dashboard/overview",
  PARTNERS: DASH.PARTNERS || "dashboard/partners",
  INSTITUTIONS: DASH.INSTITUTIONS || "dashboard/institutions",
  INSTITUTION_DETAIL: (id) =>
    DASH.INSTITUTION_DETAIL?.(id) || `dashboard/institutions/${id}/detail`,
  VERTICAL_K12: "dashboard/vertical/k12",
  VERTICAL_REVI: "dashboard/vertical/revi",
  VERTICAL_SIMULATOR: "dashboard/vertical/simulator",
  VERTICAL_OTROS: "dashboard/vertical/otros",
  TEACHERS: "dashboard/teachers",
  STUDENTS: "dashboard/students",
};

async function doFetch(url, params = {}) {
  const res = await fetch(url, params);
  const ct = res.headers.get("content-type") || "";
  const parse = ct.includes("application/json")
    ? () => res.json()
    : () => res.text();
  const data = await parse().catch(() => ({}));

  if (!res.ok) {
    const err = new Error(data?.message || `Request error ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

const authHeaders = (accessToken) =>
  accessToken ? { Authorization: `Bearer ${accessToken}` } : {};

const jsonGet = (path, accessToken) =>
  doFetch(`${ENV.BASE_API}/${path}`, {
    method: "GET",
    headers: { Accept: "application/json", ...authHeaders(accessToken) },
  });

export class DashboardApi {
  // 4 endpoints legacy (consumidos por client + Educa360-Registro segun caso)
  getOverview(accessToken) {
    return jsonGet(ROUTES.OVERVIEW, accessToken);
  }

  getPartnersList(accessToken) {
    return jsonGet(ROUTES.PARTNERS, accessToken);
  }

  getInstitutionsList(accessToken) {
    return jsonGet(ROUTES.INSTITUTIONS, accessToken);
  }

  getInstitutionDetail(accessToken, id) {
    return jsonGet(ROUTES.INSTITUTION_DETAIL(id), accessToken);
  }

  // 4 endpoints nuevos por vertical (M06 Fase 2). Solo accesibles con admin.
  // K12 y REVI aceptan ?mode=general|platform (M06 ext).
  getVerticalK12(accessToken, mode) {
    const qs = mode ? `?mode=${encodeURIComponent(mode)}` : "";
    return jsonGet(`${ROUTES.VERTICAL_K12}${qs}`, accessToken);
  }

  getVerticalRevi(accessToken, mode) {
    const qs = mode ? `?mode=${encodeURIComponent(mode)}` : "";
    return jsonGet(`${ROUTES.VERTICAL_REVI}${qs}`, accessToken);
  }

  getVerticalSimulator(accessToken) {
    return jsonGet(ROUTES.VERTICAL_SIMULATOR, accessToken);
  }

  getVerticalOtros(accessToken) {
    return jsonGet(ROUTES.VERTICAL_OTROS, accessToken);
  }

  // Listados de detalle (Fase 6) - Teachers y Students
  getTeachersList(accessToken) {
    return jsonGet(ROUTES.TEACHERS, accessToken);
  }

  getStudentsList(accessToken) {
    return jsonGet(ROUTES.STUDENTS, accessToken);
  }
}

// Instancia compartida + alias compatible con consumidores actuales que aun
// importan `{ dashboardApi }`. La instancia tiene los mismos metodos que el
// objeto plano original, mas los 4 nuevos por vertical.
export const dashboardApi = new DashboardApi();

// Exports nombrados para los consumidores legacy que importan funciones
// individuales (getOverview, etc.). Son solo proxies a la instancia.
export const getOverview = (accessToken) =>
  dashboardApi.getOverview(accessToken);
export const getPartnersList = (accessToken) =>
  dashboardApi.getPartnersList(accessToken);
export const getInstitutionsList = (accessToken) =>
  dashboardApi.getInstitutionsList(accessToken);
export const getInstitutionDetail = (accessToken, id) =>
  dashboardApi.getInstitutionDetail(accessToken, id);
